import { Check, ChevronsUpDown } from "lucide-react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { SaveIndicator } from "./SaveIndicator";
import { useSaveIndicator } from "@/hooks/useSaveIndicator";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { cn } from "@/lib/utils";

export const AutosaveFormNumberMultiselect = <
  T extends Record<string, unknown>,
>({
  className,
  fieldName,
  form,
  label,
  maxSelections,
  onSelect,
  options,
}: {
  className?: string;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  label: string;
  maxSelections?: number;
  onSelect: () => Promise<void> | void;
  options: { value: number; label: string }[];
}) => {
  const { status, trigger } = useSaveIndicator();

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        const value = field.value as number[] | undefined;

        return (
          <FormItem className={cn("w-full text-slate-700", className)}>
            <FormLabel className="inline-flex w-full items-center justify-between">
              <span className="min-w-0 truncate">{label}</span>
              <SaveIndicator status={status} />
            </FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outlineSecondary"
                    className="w-full justify-between bg-slate-50 font-normal text-black focus-visible:border-slate-400"
                  >
                    <span className="truncate">
                      {value && value.length > 0
                        ? value
                            .map(
                              (v) =>
                                options.find((o) => o.value === v)?.label ??
                                String(v),
                            )
                            .join(", ")
                        : "-"}
                    </span>
                    <ChevronsUpDown className="opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command>
                  <CommandList>
                    <CommandGroup>
                      {options.map((option) => {
                        const isSelected = value?.includes(option.value);
                        return (
                          <CommandItem
                            key={option.value}
                            className="text-slate-700"
                            onSelect={() => {
                              if (
                                !isSelected &&
                                maxSelections !== undefined &&
                                (value?.length ?? 0) >= maxSelections
                              ) {
                                return;
                              }
                              const newValue = isSelected
                                ? value?.filter((v) => v !== option.value)
                                : [...(value ?? []), option.value];
                              field.onChange(newValue);
                              trigger(onSelect());
                            }}
                          >
                            <Check
                              className={cn(!isSelected && "invisible")}
                            />
                            {option.label}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
